import {
  Button,
  ButtonEmpty,
  DatePicker,
  FieldNumber,
  Form,
  FormRow,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderTitle,
  Text,
} from '@antoree/ant-ui';
import { Session } from 'services/session';
import { yupResolver } from '@hookform/resolvers/yup';
import { HAPPENING, UNHAPPENED, UPCOMING } from 'constants/session';
import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useCurrentUser } from 'contexts';
import {
  formatUserTimezoneCaption,
  parseWallClockInUserSettings,
  VIETNAM_TZ,
} from 'utils/sessionTimezone';

export interface IFormData {
  date: string;
  time: string;
  duration: number;
}
export type EditSessionProps = {
  close: () => void;
  onUpdateSession: (duration: number, occurredAt: string) => void;
  isLoading: boolean;
  session?: Session;
};
const EditSession: React.FC<EditSessionProps> = ({
  close,
  onUpdateSession,
  isLoading,
  session,
}) => {
  const intl = useIntl();
  const { timeZone: userTimeZone } = useCurrentUser();
  const tzCaption = useMemo(() => formatUserTimezoneCaption(userTimeZone), [
    userTimeZone,
  ]);
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<IFormData>({
    mode: 'onSubmit',
    defaultValues: {
      date: '',
      time: '',
      duration: 0,
    },
    resolver: yupResolver(
      yup.object().shape({
        date: yup
          .string()
          .required(
            intl.formatMessage({
              id: 'edit_session.field_invalid',
              defaultMessage: 'This field is invalid',
            }),
          ),
        time: yup
          .string()
          .required(
            intl.formatMessage({
              id: 'edit_session.field_invalid',
              defaultMessage: 'This field is invalid',
            }),
          ),
        duration: yup
          .number()
          .required()
          .min(
            1,
            intl.formatMessage({
              id: 'edit_session.must_be_more_than_1',
              defaultMessage: 'Must be more than 1',
            }),
          ),
      }),
    ),
  });
  const onSubmit = (dataForm: IFormData) => {
    const { date, time, duration } = dataForm;
    const dateStr = moment(date).format('YYYY-MM-DD');
    const timeStr = moment(time).format('HH:mm:ss');
    // Wall clock theo profile GV; BE normalizeTeacherLocalOccurredAtToDbUtc + DateTimeHelper::fromUser.
    const occurredAt = `${dateStr} ${timeStr}`;
    onUpdateSession(+duration, occurredAt);
  };
  useEffect(() => {
    if (session) {
      reset({
        duration: moment
          .duration(
            moment(
              `${session.shortDateOccurred_at} ${session.shortTimeEndedAt}`,
            ).diff(
              `${session.shortDateOccurred_at} ${session.shortTimeStartedAt}`,
            ),
          )
          .asMinutes(),
      });
    }
  }, [session]);

  return (
    <Modal onClose={close}>
      <ModalHeader className="flex flex-col ">
        <ModalHeaderTitle className="mr-auto">
          <h1>
            {session?.happenedStatus === UNHAPPENED && (
              <FormattedMessage
                id="edit_session.create_makeup_class"
                defaultMessage="Create make-up class"
              />
            )}
          </h1>
        </ModalHeaderTitle>
        <div>
          <FormattedMessage
            id="edit_session.manual_confirm_warning"
            defaultMessage="For changed schedule sessions and make-up sessions, system will no longer automatically confirmed"
          />
        </div>
      </ModalHeader>

      <ModalBody>
        <Form
          id="edit-session"
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex gap-4">
            <FormRow
              className="flex-1"
              fullWidth
              label={
                <FormattedMessage
                  id="edit_session.session_date"
                  defaultMessage="Session date (your local calendar day)"
                />
              }
              isInvalid={!!errors.date}
              error={errors.date?.message}
              required
            >
              <Controller
                name="date"
                control={control}
                render={({ field: { value, ...field } }) => (
                  <DatePicker
                    {...field}
                    selected={value ? moment(value) : null}
                    dateFormat="DD-MM-YYYY "
                    autoComplete="off"
                  />
                )}
              />
            </FormRow>
            <FormRow
              className="flex-1"
              style={{ marginTop: 0 }}
              fullWidth
              label={
                <span>
                  <FormattedMessage
                    id="edit_session.start_time"
                    defaultMessage="Start time"
                  />
                  <span style={{ fontWeight: 'normal', opacity: 0.85 }}>
                    {' '}
                    (
                    <FormattedMessage
                      id="edit_session.local_timezone_hint"
                      defaultMessage="your local time — {tz}"
                      values={{ tz: tzCaption }}
                    />
                    )
                  </span>
                </span>
              }
              isInvalid={!!errors.time}
              error={errors.time?.message}
              required
            >
              <Controller
                name="time"
                control={control}
                render={({ field: { value, ...field } }) => (
                  <DatePicker
                    showTimeSelectOnly
                    {...field}
                    selected={value ? moment(value) : null}
                    dateFormat="HH:mm"
                    timeFormat="HH:mm"
                    autoComplete="off"
                  />
                )}
              />
            </FormRow>
          </div>
          <div className="flex mt-4 gap-4">
            <FormRow
              className="flex-1"
              fullWidth
              label={
                <FormattedMessage
                  id="edit_session.duration_label"
                  defaultMessage="Duration (how long this session takes)"
                />
              }
              isInvalid={!!errors.duration}
              error={errors.duration?.message}
              required
            >
              <Controller
                name="duration"
                control={control}
                render={({ field: { ref, ...fieldRest } }) => (
                  <FieldNumber
                    className="rounded-lg"
                    inputRef={ref}
                    {...fieldRest}
                    isInvalid={!!errors.duration}
                    fullWidth
                    placeholder={intl.formatMessage({
                      id: 'edit_session.duration_placeholder',
                      defaultMessage: 'Enter duration',
                    })}
                  />
                )}
              />
            </FormRow>
            <Text className="flex-1">
              {watch('date') && watch('time') && watch('duration') ? (
                <div style={{ lineHeight: 1.5 }}>
                  {(() => {
                    const dateStr = moment(watch('date')).format('YYYY-MM-DD');
                    const timeStr = moment(watch('time')).format('HH:mm:ss');
                    const dur = Number(watch('duration'));
                    const start = parseWallClockInUserSettings(
                      dateStr,
                      timeStr,
                      userTimeZone,
                    );
                    const end = start.clone().add(dur, 'minutes');
                    const startVn = start.clone().tz(VIETNAM_TZ);
                    const endVn = end.clone().tz(VIETNAM_TZ);
                    return (
                      <>
                        <div>
                          <strong>
                            <FormattedMessage
                              id="edit_session.preview_local"
                              defaultMessage="Your local time ({tz}):"
                              values={{ tz: tzCaption }}
                            />
                          </strong>{' '}
                          {start.format('DD/MM/YYYY HH:mm')} —{' '}
                          {end.format('DD/MM/YYYY HH:mm')}
                        </div>
                        <div style={{ marginTop: 6 }}>
                          <strong>
                            <FormattedMessage
                              id="edit_session.preview_vn"
                              defaultMessage="Vietnam time (GMT+7):"
                            />
                          </strong>{' '}
                          {startVn.format('DD/MM/YYYY HH:mm')} —{' '}
                          {endVn.format('DD/MM/YYYY HH:mm')}
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <span className="mr-2" style={{ opacity: 0.7 }}>
                  <FormattedMessage
                    id="edit_session.preview_time_placeholder"
                    defaultMessage="Select date, start time and duration to see both local and Vietnam time."
                  />
                </span>
              )}
            </Text>
          </div>
        </Form>
      </ModalBody>

      <ModalFooter>
        <ButtonEmpty onClick={close}>
          <FormattedMessage id="edit_session.cancel" defaultMessage="Cancel" />
        </ButtonEmpty>
        <Button fill isLoading={isLoading} type="submit" form="edit-session">
          <FormattedMessage id="edit_session.submit" defaultMessage="Submit" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditSession;
